<template>
  <div class="contact-wrapper">
    <div v-if="editMode" class="contact contact-edit-mode">
      <validation-observer ref="contactAddValidationRules">
        <b-row>
          <b-col cols="12" lg="8">
            <b-card no-body class="section section-overview">
              <template #header>
                <!-- <h6 class="mb-0">Kontaktuppgifter</h6> -->
              </template>
              <div class="card-body">
                <h6 class="mt0">Ladda upp bild</h6>
                <div v-if="contact" class="contact-avatar text-center">
                  <b-avatar
                    v-if="contact.avatar"
                    size="8rem"
                    :src="contactAvatar"
                    class="mb-1"
                  />
                  <b-avatar
                    v-else
                    size="8rem"
                    :text="getNameAcronym(contact)"
                    class="mb-1"
                  />
                </div>
                <b-form-group>
                  <b-form-file
                    v-model="modelImage"
                    accept="image/*"
                    id="avatar"
                    browse-text="Välj bild"
                    placeholder="Välj bild eller dra &amp; släpp här..."
                    drop-placeholder="Släpp bilden här..."
                  >
                    <template slot="file-name" slot-scope="{ names }">
                      <b-badge variant="primary">
                        {{ names[0] }}
                      </b-badge>
                      <b-badge
                        v-if="names.length > 1"
                        variant="dark"
                        class="ml-1"
                      >
                        + {{ names.length - 1 }} Filer till
                      </b-badge>
                    </template>
                  </b-form-file>
                </b-form-group>
                <h6>För och efternamn</h6>
                <b-form-group label="Förnamn" label-sr-only>
                  <validation-provider
                    #default="{ errors }"
                    name="Förnamn"
                    rules="required"
                  >
                    <b-form-input
                      v-model="contact.first_name"
                      @input="updateFirstName"
                      :state="errors.length > 0 ? false : null"
                      id="firstName"
                      placeholder="Förnamn..."
                    >
                    </b-form-input>
                    <small class="text-danger">{{ errors[0] }}</small>
                  </validation-provider>
                </b-form-group>
                <b-form-group label="Efternamn" label-sr-only>
                  <validation-provider
                    #default="{ errors }"
                    name="Efternamn"
                    rules="required"
                  >
                    <b-form-input
                      v-model="contact.last_name"
                      @input="updateLastName"
                      :state="errors.length > 0 ? false : null"
                      id="lastName"
                      placeholder="Efternamn..."
                    >
                    </b-form-input>
                    <small class="text-danger">{{ errors[0] }}</small>
                  </validation-provider>
                </b-form-group>
                <h6>Titel</h6>
                <b-form-group label="Titel" label-sr-only>
                  <b-form-input
                    v-model="contact.title"
                    @input="updateTitle"
                    placeholder="T.ex. mamma, bror, assistent, fysioterapeut etc.. (Valfritt)"
                  >
                  </b-form-input>
                </b-form-group>
                <h6>Kontaktuppgifter</h6>
                <b-form-group>
                  <b-input-group>
                    <b-input-group-prepend is-text>
                      <i class="fas fa-building fa-fw"></i>
                    </b-input-group-prepend>
                    <b-form-input
                      v-model="contact.organisation"
                      @change="updateOrganisation"
                      id="organisation"
                      placeholder="Organisation..."
                      v-b-tooltip.hover.bottom.v-primary
                      title="Var jobbar personen? Till exempel: Hjälpmedelcentralen, Bräcke, Socialtjänsten, osv."
                    >
                    </b-form-input>
                  </b-input-group>
                </b-form-group>
                <b-form-group>
                  <b-input-group>
                    <b-input-group-prepend is-text>
                      <i class="fas fa-phone fa-fw"></i>
                    </b-input-group-prepend>
                    <b-form-input
                      v-model="contact.phone"
                      @change="updatePhone"
                      id="phone"
                      placeholder="Telefon..."
                    >
                    </b-form-input>
                  </b-input-group>
                </b-form-group>
                <b-form-group>
                  <b-input-group>
                    <b-input-group-prepend is-text>
                      <i class="fas fa-mobile fa-fw"></i>
                    </b-input-group-prepend>
                    <b-form-input
                      v-model="contact.mobile"
                      @change="updateMobile"
                      id="mobile"
                      placeholder="Mobil..."
                    >
                    </b-form-input>
                  </b-input-group>
                </b-form-group>
                <b-form-group>
                  <validation-provider
                    #default="{ errors }"
                    name="Mailadress"
                    rules="email"
                  >
                    <b-input-group>
                      <b-input-group-prepend is-text>
                        <i class="fas fa-at fa-fw"></i>
                      </b-input-group-prepend>
                      <b-form-input
                        v-model="contact.email"
                        @input="updateEmail"
                        :state="errors.length > 0 ? false : null"
                        id="email"
                        placeholder="Mailadress..."
                      >
                      </b-form-input>
                    </b-input-group>
                    <small class="text-danger">{{ errors[0] }}</small>
                  </validation-provider>
                </b-form-group>
                <h6>Anteckning</h6>
                <b-form-group label="Anteckning" label-sr-only>
                  <TextEditor
                    v-on:on-body-update="updateNotes"
                    :id="contact.id"
                    :body="contact.notes"
                  />
                </b-form-group>
              </div>
            </b-card>
          </b-col>
          <b-col cols="12" lg="4">
            <b-card no-body class="section section-meta">
              <template #header>
                <h6 class="mb-0">Om kontakten</h6>
                <b-button
                  v-b-toggle="'collapse-contact-about'"
                  class="section-toggle"
                  variant="link"
                  size="sm"
                  ><i class="fas fa-chevron-down"></i
                ></b-button>
              </template>
              <b-collapse visible id="collapse-contact-about">
                <div class="card-body">
                  <h6>Kontaktgrupper</h6>
                  <b-form-group
                    v-can="$permissions.WRITE_OTHER_CONTACT"
                    label="Välj en eller flera grupper"
                    for="contactAccessGroups"
                  >
                    <validation-provider
                      #default="{ errors }"
                      name="Välj en eller flera grupper"
                      rules="required"
                    >
                      <v-select
                        @input="updateContactAccessGroups"
                        :value="chosenAccessGroups"
                        id="contactAccessGroups"
                        multiple
                        label="title"
                        :options="accessGroupOptions"
                        :class="{ 'is-invalid': !!errors.length }"
                      />
                      <small class="text-danger">{{ errors[0] }}</small>
                    </validation-provider>
                  </b-form-group>

                  <div v-if="!canUser($permissions.WRITE_OTHER_CONTACT)">
                    <span
                      v-for="accessGroup in chosenAccessGroups"
                      :key="accessGroup.value"
                    >
                      {{ accessGroup.title }},
                    </span>
                  </div>

                  <b-card-text>
                    <hr class="my-2" />
                    <h5>Behörigheter</h5>
                    <div
                      v-can="$permissions.WRITE_OTHER_CONTACT"
                      v-if="!contact.has_user && !contact.contact_code_valid"
                    >
                      <!-- Case 1: ingen inbjudan är skickad -->
                      <p>
                        {{ contact.first_name }} har inga behörigheter. Om du
                        vill att {{ contact.first_name }} ska kunna se och
                        redigera din portfölj kan du skicka en inbjudan.
                      </p>
                      <b-button
                        v-can="$permissions.WRITE_OTHER_CONTACT"
                        v-b-modal.modalSendInvitation
                        variant="primary"
                        block
                      >
                        <!-- Endast synlig om user har behörighet att hantera inbjudningar -->
                        Skicka inbjudan <i class="fas fa-user-plus"></i>
                      </b-button>
                    </div>
                    <!-- TODO: Kolla även ifall datumet stämmer -->
                    <div
                      v-if="
                        contact.contact_code &&
                        contact.contact_code_valid &&
                        !contact.has_user
                      "
                    >
                      <!-- Case 2: inbjudan är pending -->
                      <b-alert show variant="warning">
                        <div class="alert-body">
                          {{ contact.first_name }} har fått en inbjudan till
                          följande mailadress: {{ contact.email }}
                          <!-- Den epostadress som inbjudan skickades till -->
                          <br />
                          <span
                            >Inbjudan är giltig till:
                            {{ contact.contact_code_valid_to_date }}</span
                          >
                        </div>
                      </b-alert>
                      <b-button
                        v-can="$permissions.WRITE_OTHER_CONTACT"
                        @click="cancelInvite"
                        variant="danger"
                        block
                      >
                        <!-- Avbryter och raderar inbjudan, återgår till case 1. Endast synlig om user har behörighet att hantera inbjudningar -->
                        Avbryt inbjudan <i class="fas fa-user-slash"></i>
                      </b-button>
                    </div>
                    <div
                      v-can="$permissions.WRITE_OTHER_CONTACT"
                      v-if="contact.has_user"
                    >
                      <!-- Case 3: inbjudan är acceperad -->
                      <b-form-group
                        label="Välj en behörighetsgrupp"
                        for="contactPermissionGroups"
                      >
                        <validation-provider
                          #default="{ errors }"
                          name="Välj en behörighetsgrupp"
                          rules="required"
                        >
                          <v-select
                            @input="updatePermissionGroup"
                            :value="chosenEditPermissionGroup"
                            :clearable="false"
                            id="contactPermissionGroups"
                            label="title"
                            :options="permissionGroupOptions"
                            :class="{ 'is-invalid': !!errors.length }"
                          />
                          <small class="text-danger">{{ errors[0] }}</small>
                        </validation-provider>
                      </b-form-group>
                      <b-form-group
                        label="Välj en eller flera sekretessgrupper"
                        for="contactSecurityGroups"
                      >
                        <v-select
                          @input="updateSecurityGroups"
                          :value="chosenSecurityGroups"
                          id="contactSecurityGroups"
                          multiple
                          label="title"
                          :options="securityGroupOptions"
                        />
                      </b-form-group>
                      <b-button
                        v-can="$permissions.WRITE_OTHER_CONTACT"
                        variant="danger"
                        block
                      >
                        <!-- Endast synlig om user har behörighet att hantera inbjudningar -->
                        Blockera kontakt <i class="fas fa-user-slash"></i>
                      </b-button>
                    </div>
                    <div v-if="contact.user_blocked">
                      <!-- Case 4: Kontakt är blockerad -->
                      <b-alert show variant="warning">
                        <div class="alert-body">
                          {{ contact.first_name }} är blockerad och kan inte
                          längre se eller redigera portföljen.
                        </div>
                      </b-alert>
                      <b-button
                        v-if="$permissions.WRITE_OTHER_CONTACT"
                        variant="primary"
                        block
                      >
                        <!-- Endast synlig om user har behörighet att hantera inbjudningar -->
                        Avblockera kontakt <i class="fas fa-user-check"></i>
                      </b-button>
                    </div>
                    <div
                      v-if="
                        contact.has_user &&
                        !canUser($permissions.WRITE_OTHER_CONTACT)
                      "
                    >
                      <!-- Case 5: inbjudan är acceperad men user har inte behörighet att hantera inbjudningar -->
                      <p>
                        <strong>Behörighetsgrupp:</strong><br />
                        {{ chosenPermissionGroup }}
                      </p>
                      <p>
                        <strong>Sekretessgrupper:</strong><br />
                        <span
                          v-for="securityGroup in chosenSecurityGroups"
                          :key="securityGroup"
                        >
                          {{ securityGroup }},
                        </span>
                      </p>
                    </div>
                  </b-card-text>
                </div>
              </b-collapse>
            </b-card>
            <b-row class="article-actions mb-3">
              <b-col cols="12">
                <!-- <b-button variant="primary" size="lg" block >Förhandsgranska</b-button>
                          <b-button variant="primary" size="lg" block >Spara som utkast</b-button> -->
                <b-button
                  v-can:[isContactOwner]="$permissions.WRITE_OTHER_CONTACT"
                  @click="saveContact"
                  variant="success"
                  size="lg"
                  block
                  >Spara och publicera</b-button
                >
              </b-col>
            </b-row>
          </b-col>
        </b-row>
      </validation-observer>
    </div>

    <div v-else class="contact">
      <b-row v-if="contact">
        <b-col cols="12" lg="8">
          <b-card no-body class="section section-overview">
            <template #header>
              <!-- <h6 class="mb-0">Kontaktuppgifter</h6> -->
            </template>
            <div class="card-body">
              <div class="contact-presentation text-center">
                <b-avatar
                  v-if="contact"
                  size="8rem"
                  :src="contactAvatar"
                  class="mb-1"
                />

                <h3>{{ contact.first_name }} {{ contact.last_name }}</h3>
                <!-- Kontakt titel och noter -->
                <h4>{{ contact.title }}</h4>
                <!-- Kontakt titel och noter -->
              </div>
              <h6 class="mt-2">Kontaktuppgifter</h6>
              <ul class="contact-info">
                <li v-if="contact.organisation">
                  <span class="text-success mr-1"
                    ><i class="fas fa-building fa-fw"></i></span
                  >{{ contact.organisation }}
                </li>
                <li v-if="contact.phone">
                  <span class="text-success mr-1"
                    ><i class="fas fa-phone fa-fw"></i></span
                  ><a :href="'tel:' + contact.phone">{{ contact.phone }}</a>
                </li>
                <li v-if="contact.mobile">
                  <span class="text-success mr-1"
                    ><i class="fas fa-mobile fa-fw"></i></span
                  ><a :href="'tel:' + contact.mobile">{{ contact.mobile }}</a>
                </li>
                <li v-if="contact.mobile">
                  <span class="text-success mr-1">
                    <i class="fas fa-comment-dots fa-fw"></i></span
                  ><a :href="'sms:' + contact.mobile">{{ contact.mobile }}</a>
                </li>
                <li v-if="contact.email">
                  <span class="text-success mr-1">
                    <i class="fas fa-at fa-fw"></i></span
                  ><a :href="'mailto:' + contact.email">{{ contact.email }}</a>
                </li>
              </ul>
              <h6 class="mt-2">Anteckningar</h6>
              <div v-html="contact.notes" class="contact-notes-text"></div>
            </div>
          </b-card>
        </b-col>
        <b-col cols="12" lg="4">
          <b-card no-body class="section section-meta">
            <template #header>
              <h6 class="mb-0">Om kontakten</h6>
              <b-button
                v-b-toggle="'collapse-contact-about'"
                class="section-toggle"
                variant="link"
                size="sm"
                ><i class="fas fa-chevron-down"></i
              ></b-button>
            </template>
            <b-collapse visible id="collapse-contact-about">
              <div class="card-body">
                <b-card-text>
                  <h6>Skapad {{ formatDate(contact.published_at) }} av:</h6>
                  <!-- Här behöver vi hämta information från users och loopa igenom -->
                  <b-avatar
                    v-if="createdByContact.avatar"
                    size="md"
                    v-b-tooltip.hover
                    class="pull-up"
                    :title="
                      createdByContact.first_name +
                      ' ' +
                      createdByContact.last_name
                    "
                    :src="createdByAvatar"
                  />
                  <b-avatar
                    v-else-if="!createdByContact.avatar"
                    size="md"
                    v-b-tooltip.hover
                    class="pull-up"
                    :title="
                      createdByContact.first_name +
                      ' ' +
                      createdByContact.last_name
                    "
                    :text="
                      createdByContact.first_name[0] +
                      createdByContact.last_name[0]
                    "
                  />
                  {{ contact.created_by }}
                  <small>{{
                    createdByContact.first_name +
                    " " +
                    createdByContact.last_name
                  }}</small>
                </b-card-text>
                <b-card-text>
                  <h6>
                    Senast uppdaterad {{ formatDate(contact.updated_at) }} av:
                  </h6>
                  <!-- Här behöver vi hämta information från users och loopa igenom -->
                  <b-avatar
                    v-if="updatedByContact.avatar"
                    size="md"
                    v-b-tooltip.hover
                    class="pull-up"
                    :title="
                      updatedByContact.first_name +
                      ' ' +
                      updatedByContact.last_name
                    "
                    :src="updatedByAvatar"
                  />
                  <b-avatar
                    v-else-if="!updatedByContact.avatar"
                    size="md"
                    v-b-tooltip.hover
                    class="pull-up"
                    :title="
                      updatedByContact.first_name +
                      ' ' +
                      updatedByContact.last_name
                    "
                    :text="
                      updatedByContact.first_name[0] +
                      updatedByContact.last_name[0]
                    "
                  />
                  {{ contact.updated_by }}
                  <small>{{
                    updatedByContact.first_name +
                    " " +
                    updatedByContact.last_name
                  }}</small>
                </b-card-text>
                <b-card-text>
                  <p>
                    <strong>Kontaktgrupper:</strong
                    ><br /><!-- Vilken/vilka accessgrupper är kontakten med i -->
                    <span
                      v-for="group in chosenAccessGroups"
                      :key="group.title"
                    >
                      {{ group.title }},
                    </span>
                  </p>
                </b-card-text>

                <b-card-text>
                  <hr class="my-2" />
                  <h5>Behörigheter</h5>
                  <div
                    v-can="$permissions.WRITE_OTHER_CONTACT"
                    v-if="!contact.contact_code_valid && !contact.has_user"
                  >
                    <!-- Case 1: ingen inbjudan är skickad -->
                    <p>
                      {{ contact.first_name }} har inga behörigheter. Om du vill
                      att {{ contact.first_name }} ska kunna se och redigera
                      portföljen kan du skicka en inbjudan.
                    </p>
                    <b-button
                      v-can="$permissions.WRITE_OTHER_CONTACT"
                      v-b-modal.modalSendInvitation
                      variant="primary"
                      block
                    >
                      <!-- Endast synlig om user har behörighet att hantera inbjudningar -->
                      Skicka inbjudan <i class="fas fa-user-plus"></i>
                    </b-button>
                  </div>
                  <!-- TODO: Kolla även så tiden inte faller -->
                  <div
                    v-can="$permissions.WRITE_OTHER_CONTACT"
                    v-if="
                      contact.contact_code_valid &&
                      contact.contact_code &&
                      !contact.has_user
                    "
                  >
                    <!-- Case 2: inbjudan är pending -->
                    <b-alert show variant="warning">
                      <div class="alert-body">
                        {{ contact.first_name }} har fått en inbjudan till
                        följande mailadress: {{ contact.email }}
                        <!-- Den epostadress som inbjudan skickades till -->
                        <br />
                        <span
                          >Inbjudan är giltig till:
                          {{ contact.contact_code_valid_to_date }}</span
                        >
                      </div>
                    </b-alert>
                    <b-button
                      v-can="$permissions.WRITE_OTHER_CONTACT"
                      @click="cancelInvite"
                      variant="danger"
                      block
                    >
                      <!-- Avbryter och raderar inbjudan, återgår till case 1. Endast synlig om user har behörighet att hantera inbjudningar -->
                      Avbryt inbjudan <i class="fas fa-user-slash"></i>
                    </b-button>
                  </div>
                  <div v-if="contact.has_user">
                    <!-- Case 3: inbjudan är acceperad -->
                    <p>
                      <strong>Behörighetsgrupp:</strong><br />
                      {{ chosenPermissionGroup }}
                    </p>
                    <p>
                      <strong>Sekretessgrupper:</strong><br />
                      <span
                        v-for="securityGroup in chosenSecurityGroups"
                        :key="securityGroup"
                      >
                        {{ securityGroup }},
                      </span>
                    </p>
                  </div>
                </b-card-text>
              </div>
            </b-collapse>
          </b-card>
          <b-row class="article-actions mb-3">
            <b-col cols="12">
              <b-button
                v-can-write:[isContactOwner]="$permissions.WRITE_OTHER_CONTACT"
                @click="startEditMode"
                variant="primary"
                size="lg"
                block
                >Redigera <i class="fas fa-edit"></i
              ></b-button>
              <b-button
                v-if="!isContactOwner"
                v-can-delete="$permissions.WRITE_OTHER_CONTACT"
                @click="deleteContactAlert"
                variant="danger"
                size="lg"
                block
                >Radera <i class="fas fa-trash"></i
              ></b-button>
              <!-- <b-button v-if="!previewMode" @click="editArticle" variant="primary" size="lg" block >Redigera <i class="fas fa-edit"></i></b-button>
                          <b-button v-if="!previewMode && article.favorite == false || isFavorite == false" @click="setFavorite" variant="primary" size="lg" block>Gör till favorit <i class="fas fa-star"></i></b-button>
                          <b-button v-if="!previewMode && article.favorite == true || isFavorite == true" @click="unsetFavorite" variant="warning" size="lg" block>Ta bort som favorit <i class="fas fa-star"></i></b-button>
                          <b-button v-if="!previewMode" @click="deleteArticleAlert" variant="danger" size="lg" block >Radera <i class="fas fa-trash"></i></b-button> -->
            </b-col>
          </b-row>
        </b-col>
      </b-row>
    </div>

    <!--  Modals -->
    <b-modal
      v-if="contact"
      id="modalSendInvitation"
      title="Skicka inbjudan"
      cancel-title="Avbryt"
      ok-title="Skicka inbjudan"
      size="lg"
      no-close-on-backdrop
      @ok="sendInvite"
    >
      <b-alert show variant="primary">
        <h4 class="alert-heading">
          <i class="fas fa-info-circle"></i> Information
        </h4>
        <div class="alert-body">
          <p>
            Genom att skicka en inbjudan gör du det möjligt för
            {{ contact.first_name }} att se och redigera portföljen.
          </p>
          <p>
            Inbjudan är personlig och ska inte gå till en företagsadress,
            verksamhetsadress eller mailadress med flera mottagare.
          </p>
          <p>Gör så här:</p>
          <ol>
            <li>Kontrollera att du angett rätt mailadress.</li>
            <li>
              Välj vilken behörighetsgrupp du vill ge till
              {{ contact.first_name }}. Behörighetsgruppen styr vilka delar av
              portföljen som {{ contact.first_name }} får se och redigera.
            </li>
            <li>
              Välj vilken eller vilka sekretessgrupper du vill ge till
              {{ contact.first_name }}. Sekretessgrupper styr vilket innehåll i
              portföljen som är synligt för {{ contact.first_name }}.
            </li>
            <li>Klicka på knappen "Skicka inbjudan".</li>
          </ol>
        </div>
      </b-alert>
      <b-form-group label="Skicka inbjudan till" for="recipientEmail">
        <validation-provider
          #default="{ errors }"
          name="Skicka inbjudan till"
          rules="required|email"
        >
          <b-form-input
            :value="contact.email"
            @input="updateEmail"
            :state="errors.length > 0 ? false : null"
            id="recipientEmail"
            placeholder="Mailadress..."
          >
            <!--  Hämta från contact.email ska vara redigerbar om det skulle va fel epost där -->
          </b-form-input>
          <small class="text-danger">{{ errors[0] }}</small>
        </validation-provider>
      </b-form-group>
      <h6>Behörighetsgrupp</h6>
      <b-form-group label="Välj en behörighetsnivå" for="contactPermissions">
        <validation-provider
          #default="{ errors }"
          name="Välj en behörighetsnivå"
          rules="required"
        >
          <v-select
            @input="updatePermissionGroup"
            :value="chosenEditPermissionGroup"
            :clearable="false"
            id="contactAccessGroups"
            label="title"
            :options="permissionGroupOptions"
            :class="{ 'is-invalid': !!errors.length }"
          />
          <small class="text-danger">{{ errors[0] }}</small>
        </validation-provider>
      </b-form-group>

      <h6>Grupper</h6>
      <b-form-group
        label="Välj en eller flera grupper"
        for="contactAccessGroups"
      >
        <validation-provider
          #default="{ errors }"
          name="Välj en eller flera grupper"
          rules="required"
        >
          <v-select
            @input="updateSecurityGroups"
            :value="chosenSecurityGroups"
            id="contactAccessGroups"
            multiple
            label="title"
            :options="securityGroupOptions"
            :class="{ 'is-invalid': !!errors.length }"
          />
          <small class="text-danger">{{ errors[0] }}</small>
        </validation-provider>
      </b-form-group>
    </b-modal>
  </div>
</template>

<script>
import { ValidationObserver, ValidationProvider, localize } from "vee-validate";
import { required, email } from "@validations";
import {
  BRow,
  BCol,
  BCard,
  BCardText,
  BCollapse,
  BButton,
  BAvatar,
  BFormGroup,
  BInputGroup,
  BInputGroupPrepend,
  BFormInput,
  BFormFile,
  BBadge,
  VBTooltip,
  BAlert,
  BModal,
  VBModal,
} from "bootstrap-vue";
import TextEditor from "@/components/quillEditor/TextEditor";
import vSelect from "vue-select";
import moment from "moment";
import { mapGetters, mapActions } from "vuex";
import { uuid } from "vue-uuid";
import mailService from "@/services/mail.service";
import contactService from "@/services/contact.service";
import canDirective from "@/UserPermissions/v-can_Directive";
import axios from "axios";
import contactPermissionsService from "@/services/userPermissionsToPortfolio.service";

export default {
  props: ["contactId", "editMode"],
  data() {
    return {
      modelImage: null,
      locale: "sv",
      required,
      email,
      selectedSecurityGroups: [],
      contactPermissions: {},
      contactAvatar: null,
      createdByAvatar: null,
      updatedByAvatar: null,
    };
  },
  components: {
    BRow,
    BCol,
    BCard,
    BCardText,
    BCollapse,
    BButton,
    BAvatar,
    BFormGroup,
    BInputGroup,
    BInputGroupPrepend,
    BFormInput,
    BFormFile,
    BBadge,
    BAlert,
    BModal,
    TextEditor,
    vSelect,
    ValidationObserver,
    ValidationProvider,
  },
  directives: {
    "b-tooltip": VBTooltip,
    "b-modal": VBModal,
  },

  computed: {
    ...mapGetters("editContact", ["getContact"]),
    ...mapGetters("editUserPermissions", ["getEditContactPermissions"]),
    ...mapGetters([
      "getCurrentContact",
      "getActivePortfolio",
      "getContactById",
      "getPermissionsForContact",
    ]),
    isContactOwner: function () {
      const currentContact = this.getCurrentContact;

      return currentContact && currentContact.id == this.contactId;
    },
    contact: function () {
      if (this.editMode) {
        return this.getContact;
      }

      return this.$store.getters.getContactById(this.contactId);
    },
    chosenSecurityGroups: function () {
      return this.getContactsSecurityGroups();
    },
    securityGroupOptions: function () {
      return this.getSecurityGroupsOptions();
    },
    permissionGroupOptions: function () {
      return this.getPermissionGroupOptions()
        .filter((p) => p.Available !== false)
        .map((perm) => perm.SwedishText);
    },
    chosenPermissionGroup: function () {
      return this.getChosenPermissionGroup();
    },
    chosenEditPermissionGroup: function () {
      return this.getChosenEditPermissionGroup();
    },
    accessGroupOptions() {
      return this.getAccessGroupOptions();
    },
    chosenAccessGroups() {
      return this.getChosenAccessGroups();
    },
    createdByContact() {
      return this.$store.getters.getContactById(
        this.contact.created_by_contact || this.$store.getters.getOwner.id
      );
    },
    updatedByContact() {
      return this.$store.getters.getContactById(
        this.contact.updated_by_contact || this.$store.getters.getOwner.id
      );
    },
  },
  watch: {
    modelImage: function (newImage, oldImage) {
      if (!newImage) return;
      if (newImage == oldImage) return;
      this.updateAvatar(newImage);
    },
    contact(contact) {
      if (contact.avatar) {
        this.fetchImageWithToken(contact.avatar.medium, "contactAvatar");
      }
    },
    createdByAvatar(createdByContact) {
      if (createdByContact.avatar) {
        console.log(createdByContact.avatar.small);
        this.fetchImageWithToken(
          createdByContact.avatar.small,
          "createdByAvatar"
        );
      }
    },
    updatedByAvatar(updatedByContact) {
      if (updatedByContact.avatar) {
        this.fetchImageWithToken(
          updatedByContact.avatar.small,
          "updatedByAvatar"
        );
      }
    },
  },
  methods: {
    ...mapActions("editContact", [
      "updateEditContact",
      "initContactEdit",
      "updateAccessGroups",
      "uploadContactAvatar",
      "publishEditContact",
    ]),
    ...mapActions("editUserPermissions", [
      "initContactPermissionsEdit",
      "updatePermissions",
      "clearContactPermissionsEdit",
      "publishContactPermissionsEdit",
    ]),
    async fetchImageWithToken(imageUrl, type) {
      try {
        const token = JSON.parse(localStorage.getItem("user")).jwt;

        const response = await axios.get(imageUrl, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
          responseType: "blob",
        });
        // use the set $ to upade the imageUrls
        if (type === "contactAvatar") {
          this.contactAvatar = URL.createObjectURL(response.data);
        } else if (type === "createdByAvatar") {
          this.createdByAvatar = URL.createObjectURL(response.data);
        } else if (type === "updatedByAvatar") {
          this.updatedByAvatar = URL.createObjectURL(response.data);
        }
      } catch (error) {
        return "";
      }
    },
    updateFirstName(name) {
      let contact = this.contact;
      contact.first_name = name;
    },
    updateLastName(name) {
      let contact = this.contact;
      contact.last_name = name;
    },
    updateTitle: function (title) {
      let contact = this.contact;
      contact.title = title;
    },
    updateNotes: function (notes) {
      let contact = this.contact;
      contact.notes = notes;
    },
    updateOrganisation(organisation) {
      let contact = this.contact;
      contact.organisation = organisation;
    },
    updatePhone(phone) {
      let contact = this.contact;
      contact.phone = phone;
    },
    updateMobile(mobile) {
      let contact = this.contact;
      contact.mobile = mobile;
    },
    updateEmail(email) {
      let contact = this.contact;
      contact.email = email;
    },
    async updateAvatar(image) {
      const res = await this.uploadContactAvatar(image);
      await this.fetchImageWithToken(res.medium, "contactAvatar");
    },
    updateContactAccessGroups(chosenAccessGroups) {
      let ids = [];
      for (let i = 0; i < chosenAccessGroups.length; i++) {
        ids.push(chosenAccessGroups[i].value);
      }

      this.updateAccessGroups(ids);
    },
    getAccessGroupOptions() {
      let options = [];
      let accessGroups = this.$store.getters.getAccessGroups;

      for (let i = 0; i < accessGroups.length; i++) {
        let option = {
          value: accessGroups[i].id,
          title: accessGroups[i].title,
        };
        options.push(option);
      }
      return options;
    },
    getChosenAccessGroups() {
      let accessGroupCount = this.contact.access_groups
        ? this.contact.access_groups.length
        : 0;
      let accessGroups = [];

      for (let i = 0; i < accessGroupCount; i++) {
        let accessGroup = this.accessGroupOptions.find(
          (a) => a.value == this.contact.access_groups[i].id
        );
        if (accessGroup) {
          accessGroups.push(accessGroup);
        }
      }
      return accessGroups;
    },
    canUser(permission) {
      return canDirective.userCan(permission);
    },
    updateSecurityGroups(permissionsInText) {
      let currentPermissions = this.getEditContactPermissions.permissions;
      const allSecurityGroups = Object.values(this.$accessGroup);

      if (permissionsInText.length > this.selectedSecurityGroups.length) {
        const toAddPermission = allSecurityGroups
          .filter((obj) => permissionsInText.includes(obj.SwedishText))
          .map((perm) => perm.Permissions)
          .reduce((a, b) => a + b, 0);
        currentPermissions |= toAddPermission;
      } else {
        const nameOfPermissions = this.selectedSecurityGroups.filter(
          (perm) => !permissionsInText.includes(perm)
        )[0];
        const toRemovePermission = allSecurityGroups
          .filter((perm) => perm.SwedishText === nameOfPermissions)
          .map((perm) => perm.Permissions);
        currentPermissions &= ~toRemovePermission;
      }

      this.selectedSecurityGroups = permissionsInText;
      this.updatePermissions(currentPermissions);
    },
    getContactsSecurityGroups() {
      const contactPerms = this.contactPermissions.permissions;
      const values = Object.values(this.$accessGroup)
        .filter((perm) => perm.Permissions & contactPerms)
        .map((perm) => perm.SwedishText);
      return values;
    },
    getSecurityGroupsOptions() {
      return Object.values(this.$accessGroup).map((perm) => perm.SwedishText);
    },
    getPermissionGroupOptions() {
      return Object.values(this.$permissionGroup);
    },
    updatePermissionGroup(permissionGroupInText) {
      let permissionGroup = Object.values(this.$permissionGroup).filter(
        (perm) => perm.SwedishText === permissionGroupInText
      )[0].Permissions;

      const allSecurityGroups = Object.values(this.$accessGroup);
      const securityGroupsValue = allSecurityGroups
        .filter((obj) =>
          this.getContactsSecurityGroups().includes(obj.SwedishText)
        )
        .map((perm) => perm.Permissions)
        .reduce((a, b) => a + b, 0);

      permissionGroup |= securityGroupsValue;
      this.updatePermissions(permissionGroup);
    },
    getChosenEditPermissionGroup() {
      const contactPerms = this.getEditContactPermissions.permissions;
      let allPermissionGroups = Object.values(this.$permissionGroup);
      const currentPossiblePermissionGroups = allPermissionGroups
        .filter(
          (perm) => (perm.Permissions & contactPerms) === perm.Permissions
        )
        .sort((a, b) => a.Permissions - b.Permissions);

      const permissionGroupInText =
        currentPossiblePermissionGroups.pop()?.SwedishText;
      return permissionGroupInText || "";
    },
    getChosenPermissionGroup() {
      const contactPerms = this.$store.getters.getPermissionsForContact(
        this.contactId
      ).permissions;
      let allPermissionGroups = Object.values(this.$permissionGroup);
      const currentPossiblePermissionGroups = allPermissionGroups
        .filter(
          (perm) => (perm.Permissions & contactPerms) === perm.Permissions
        )
        .sort((a, b) => a.Permissions - b.Permissions);

      const permissionGroupInText =
        currentPossiblePermissionGroups.pop()?.SwedishText;
      return permissionGroupInText || "";
    },
    saveContact() {
      const that = this;
      this.$refs.contactAddValidationRules.validate().then(function (success) {
        if (success) {
          that.contact.updated_by_contact =
            that.$store.getters.getCurrentContact.id;
          that.updateEditContact(that.contact);

          that.publishEditContact().then(function () {
            that
              .publishContactPermissionsEdit(that.contactPermissions.id)
              .then(() => {
                that.goToContactDetails();
              });
          });
        }
      });
    },
    deleteContact() {
      this.redirectPage = "to";
      this.$store.dispatch("deleteContact", this.contact);
      this.$router.push({ name: "contacts" });
    },
    startEditMode() {
      this.$router.push({
        name: "contact-add",
        params: { contactId: this.contact.id, isNew: false },
      });
    },
    goToContactDetails() {
      this.$emit("go-to-contact-details", { title: "", id: this.contact.id });
    },
    formatDate(date) {
      return moment(date).format("YYYY-MM-DD");
    },
    deleteContactAlert() {
      this.$swal({
        title: "Vill du verkligen radera kontakten?",
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: "Radera kontakt",
        cancelButtonText: "Avbryt",
        customClass: {
          confirmButton: "btn btn-primary",
          cancelButton: "btn btn-outline-danger ml-1",
        },
        buttonsStyling: false,
      }).then((result) => {
        if (result.value) {
          this.deleteContact();
        }
      });
    },
    cancelInvite: function () {
      let contact = this.contact;
      contact.contact_code_valid = false;
      contactService.update(this.contact).then((result) => {
        if (result) {
          this.updateEditContact(contact);
        }
      });
    },
    sendInvite: function () {
      const ownerContact = this.$store.getters.getOwner;
      this.contact.contact_code = uuid.v1();
      this.contact.contact_code_valid = true;
      let date = new Date();
      date.setDate(date.getDate() + 30);
      this.contact.contact_code_valid_to_date = date
        .toISOString()
        .split("T")[0];

      const emailSubstituteFields = {
        First_Name: this.contact.first_name,
        Last_Name: this.contact.last_name,
        Owner_First_Name: ownerContact.first_name,
        Guid: this.contact.contact_code,
      };

      contactService.update(this.contact).then((result) => {
        if (result) {
          this.updateEditContact(this.contact);
          const permissionData = {
            permissions: this.contactPermissions.permissions,
            portfolio: this.getActivePortfolio.id,
            contact: this.contact.id,
          };
          contactPermissionsService.update(
            this.getPermissionsForContact(this.contactId).id,
            permissionData
          );
          mailService.sendEmail(
            "portfolio_invitation",
            this.contact.email,
            emailSubstituteFields
          );
        }
      });
    },
    getNameAcronym: function (contact) {
      if (contact.first_name?.length > 0 && contact.last_name.length > 0)
        return contact.first_name[0] + contact.last_name[0];

      return "A1";
    },
  },
  async mounted() {
    if (this.contact.avatar) {
      await this.fetchImageWithToken(
        this.contact.avatar.medium,
        "contactAvatar"
      );
    }
    if (this.createdByContact.avatar) {
      this.fetchImageWithToken(
        this.createdByContact.avatar.small,
        "createdByAvatar"
      );
    }
    if (this.updatedByContact.avatar) {
      this.fetchImageWithToken(
        this.updatedByContact.avatar.small,
        "updatedByAvatar"
      );
    }

    localize(this.locale);
    this.$nextTick(function () {
      this.initContactPermissionsEdit(this.contactId).then(() => {
        this.contactPermissions = this.getEditContactPermissions;
        const contactPerms = this.contactPermissions.permissions;
        this.selectedSecurityGroups =
          Object.values(this.$accessGroup).filter(
            (perm) => (perm.Permissions & contactPerms) !== 0
          ).SwedishText || [];
      });
    });
  },
  beforeUpdate() {
    this.selectedSecurityGroups = this.getContactsSecurityGroups();
  },
  destroyed() {
    this.clearContactPermissionsEdit();
  },
};
</script>

<style scoped>
ul.contact-info {
  margin: 0;
  padding: 0;
  list-style-type: none;
}
ul.contact-info li {
  display: flex;
  align-items: center;
  padding: 1rem;
  list-style-type: none;
  border-top: 1px solid #ebe9f1;
}
.dark-layout ul.contact-info li {
  border-color: #3b4253;
}
ul.contact-info li:first-child {
  border-top: 0;
}
ul.contact-info li span {
  list-style-type: none;
  font-size: 1.5rem;
}

.ce-list-item {
  display: flex;
  align-items: center;
  padding: 0.25rem;
  border-bottom: 1px solid #ebe9f1;
}
.ce-list-item-prefix {
  padding: 0.75rem 0;
}
.ce-list-item-prefix img {
  max-width: 5.25rem;
}
.ce-list-item-desc {
  padding: 0.75rem 0 0.75rem 0.75rem;
}
.ce-list-item-action {
  padding: 0.75rem 0;
}
.ce-list-item-desc {
  margin-right: auto;
}
.file-preview img {
  max-width: 140px;
}
</style>
